import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { events } from '../data/mock';
import { Send, X } from 'lucide-react';

const EnquiryForm = ({ selectedEvents, onRemoveEvent }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    location: '',
    message: ''
  });

  const selectedEventDetails = events.filter(e => selectedEvents.includes(e.id));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section 
      id="enquiry" 
      className="py-12 bg-gradient-to-b from-white via-green-50/20 to-white"
      aria-labelledby="enquiry-heading"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 
            id="enquiry-heading"
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
          >
            Let's <span className="text-green-800">Connect</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Fill out the form below and our team will get back to you.
          </p>
        </div>

        <Card className="border-2 border-green-200 bg-white shadow-xl rounded-3xl overflow-hidden">
          <CardContent className="p-6 md:p-8">
            <form 
              name="CONTACT_FORM"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              action="/thank-you"
              className="space-y-6"
            >
              {/* Hidden field for Netlify form name */}
              <input type="hidden" name="form-name" value="CONTACT_FORM" />
              
              {/* Honeypot field for spam protection */}
              <p className="hidden" aria-hidden="true">
                <label>
                  Don't fill this out if you're human: 
                  <input name="bot-field" tabIndex="-1" autoComplete="off" />
                </label>
              </p>

              {/* Selected Events Display */}
              {selectedEventDetails.length > 0 && (
                <div className="bg-green-50 rounded-2xl p-5 border-2 border-green-200">
                  <Label className="text-sm font-bold text-green-900 mb-3 block">
                    Selected Events ({selectedEventDetails.length})
                  </Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedEventDetails.map(event => (
                      <span 
                        key={event.id}
                        className="inline-flex items-center bg-green-800 text-white pr-1 py-2 px-3 rounded-full text-sm font-medium shadow-md"
                      >
                        {event.title}
                        <button
                          type="button"
                          onClick={() => onRemoveEvent(event.id)}
                          className="ml-2 p-1 hover:bg-green-700 rounded-full transition-colors"
                          aria-label={`Remove ${event.title}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  {/* Hidden input for selected events */}
                  <input 
                    type="hidden" 
                    name="selectedEvents" 
                    value={selectedEventDetails.map(e => e.title).join(', ')} 
                  />
                </div>
              )}

              {selectedEventDetails.length === 0 && (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-5 text-amber-800 text-center">
                  <p className="font-bold text-lg">No events selected</p>
                  <p className="text-sm mt-1">
                    Please <a href="#events" className="underline font-bold text-amber-900 hover:text-amber-700">select events</a> from above before submitting.
                  </p>
                </div>
              )}

              {/* Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="companyName" className="text-gray-700 font-semibold">
                    Company Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="companyName"
                    name="companyName"
                    type="text"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Enter your company name"
                    required
                    className="border-2 border-gray-200 focus:border-green-500 focus:ring-green-500 rounded-xl py-3"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactPerson" className="text-gray-700 font-semibold">
                    Contact Person <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contactPerson"
                    name="contactPerson"
                    type="text"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    placeholder="Your full name"
                    required
                    className="border-2 border-gray-200 focus:border-green-500 focus:ring-green-500 rounded-xl py-3"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-semibold">
                    Email Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    required
                    className="border-2 border-gray-200 focus:border-green-500 focus:ring-green-500 rounded-xl py-3"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-700 font-semibold">
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 XXXXX XXXXX"
                    className="border-2 border-gray-200 focus:border-green-500 focus:ring-green-500 rounded-xl py-3"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="location" className="text-gray-700 font-semibold">
                    Location
                  </Label>
                  <Input
                    id="location"
                    name="location"
                    type="text"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="City, Country"
                    className="border-2 border-gray-200 focus:border-green-500 focus:ring-green-500 rounded-xl py-3"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="message" className="text-gray-700 font-semibold">
                    Additional Requirements
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your specific requirements, preferences, or any questions you have..."
                    rows={4}
                    className="border-2 border-gray-200 focus:border-green-500 focus:ring-green-500 resize-none rounded-xl"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={selectedEvents.length === 0}
                className="w-full bg-green-800 hover:bg-green-900 text-white py-7 text-lg font-bold shadow-xl hover:shadow-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed rounded-xl"
              >
                <span className="flex items-center justify-center gap-2">
                  <Send className="w-5 h-5" aria-hidden="true" />
                  Submit
                </span>
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default EnquiryForm;
